import { useState } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";
import { Button } from "~/components/Button";
import { BiztripLogo } from "~/components/BiztripLogo";
import { StepperProgressMobile } from "~/components/StepperProgress";
import { Topography } from "~/components/Topography";
import { styled, keyframes } from "~/application/theme";
import { FormControl, FieldLabel } from "~/components/FormControl";
import { PhoneInput, TextInput, MaskedInput, NumberInput } from "~/components/Input";
import { MaskUtils } from "~/application/utils";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { useAuth } from "~/presentation/core/contexts/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { signUpService } from "~/application/usecases";
import { snackbarService } from "~/components/SnackbarStack";
import { logError } from "~/presentation/shared/utils/errors";
import { log } from "~/utils/log";
import { AgencyEmployeeType } from "~/application/types";

const LOG_TAG = "SignUp/SignUpPage";

const SIGNUP_STEPS = ["Início", "Dados Contato", "Dados Empresa"];

const stepIn = keyframes({
  "0%": { opacity: 0, transform: "translateX(24px)" },
  "100%": { opacity: 1, transform: "translateX(0)" },
});

const FieldError = ({ name }: { name: keyof SignUpForm }) => {
  const {
    formState: { errors },
  } = useFormContext<SignUpForm>();

  const message = errors[name]?.message;

  if (!message) {
    return null;
  }

  return (
    <Text as="span" size="2" css={{ color: "$error-600" }}>
      {String(message)}
    </Text>
  );
};

const signUpSchema = yup.object().shape({
  companyName: yup.string().required("A razão social é obrigatória"),
  tradingName: yup.string().required("O nome fantasia é obrigatório"),
  email: yup.string().required("O e-mail é obrigatório").email("E-mail inválido"),
  phone: yup
    .string()
    .test(
      "is-valid-phone",
      "Telefone inválido",
      (value) => (value?.replace(/\D/g, "").length ?? 0) >= 10,
    )
    .required("O telefone é obrigatório"),
  cnpj: yup
    .string()
    .test(
      "is-valid-cnpj",
      "CNPJ inválido",
      (value) => (value?.replace(/\D/g, "").length ?? 0) === 14,
    )
    .required("O CNPJ é obrigatório"),
  averageTravelSpend: yup
    .number()
    .typeError("O gasto médio em viagens é obrigatório")
    .required("O gasto médio em viagens é obrigatório")
    .min(0),
  referredBy: yup.string().required("O indicado por é obrigatório"),
});

export type SignUpForm = yup.InferType<typeof signUpSchema>;

const PageContainer = styled("div", {
  width: "100vw",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  position: "relative",
  overflow: "hidden",
  padding: "$4",
});

const BackgroundWrapper = styled("div", {
  position: "absolute",
  inset: 0,
  zIndex: 0,
});

const CardContainer = styled("div", {
  backgroundColor: "$neutrals-white",
  borderRadius: "$mdlg",
  boxShadow: "0 4px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
  width: "100%",
  maxWidth: "480px",
  padding: "$12 $12",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "$6",
  position: "relative",
  zIndex: 1,
  "@mxsm": {
    padding: "$8 $6",
    maxWidth: "100%",
  },
});

const StepContent = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "$6",
  animation: `${stepIn} 0.3s ease-out`,
});

const LogoutButtonWrapper = styled("div", {
  position: "absolute",
  top: "$4",
  right: "$4",
  zIndex: 10,
});

export const SignUpPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [result, setResult] = useState<{ resetPasswordUrl: string; customerUuid: string } | null>(
    null,
  );
  const { user } = useUser();
  const { signOut } = useAuth();

  const methods = useForm<SignUpForm>({
    resolver: yupResolver(signUpSchema),
    defaultValues: {
      companyName: "",
      tradingName: "",
      email: "",
      phone: "",
      cnpj: "",
      averageTravelSpend: undefined,
      referredBy: "",
    },
  });
  const { control, handleSubmit, trigger } = methods;

  const { mutate: handleOnboard, isLoading: isOnboarding } = useMutation(
    (data: SignUpForm) =>
      signUpService.onboardCustomer(user.agency.uuid, {
        companyName: data.companyName,
        tradingName: data.tradingName,
        email: data.email,
        phone: data.phone,
        cnpj: data.cnpj,
        averageTravelSpend: data.averageTravelSpend,
        referredBy: data.referredBy || null,
      }),
    {
      onSuccess: (data) => {
        log.i(LOG_TAG, `Customer onboarded successfully`);
        setResult({
          resetPasswordUrl: data.resetPasswordUrl,
          customerUuid: data.customerUuid,
        });
        setIsSuccess(true);
        setActiveStep(3);
        snackbarService.showSnackbar("Empresa criada com sucesso", "success");
      },
      onError: (error: { message: string }) => {
        logError({
          error,
          logTag: LOG_TAG,
          defaultErrorMessage: "Falha ao criar empresa",
        });
      },
    },
  );

  const goToContact = () => setActiveStep(1);
  const goToCompany = async () => {
    const isValid = await trigger(["cnpj", "companyName", "tradingName", "phone"]);
    if (isValid) {
      setActiveStep(2);
    }
  };
  const goBack = () => setActiveStep((step) => Math.max(0, step - 1));

  if (user.type !== AgencyEmployeeType.COMMERCIAL) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <PageContainer>
        <LogoutButtonWrapper>
          <Button variant="outline" size="md" rounded onClick={signOut}>
            Sair
          </Button>
        </LogoutButtonWrapper>
        <BackgroundWrapper>
          <Topography lowColor="#3B82F6" midColor="#3B82F6" highColor="#ffffff" scale={2} />
        </BackgroundWrapper>

        <CardContainer>
          <StepperProgressMobile
            steps={SIGNUP_STEPS}
            activeStep={activeStep}
            allowNavigation={false}
          />

          {activeStep === 0 && (
            <StepContent>
              <Flex
                direction="column"
                align="center"
                gap="2"
                css={{ mt: "$2", mb: "$2", width: "100%" }}
              >
                <Text
                  size="5"
                  fw="600"
                  css={{ color: "$neutrals-darkest", lineHeight: 1.3, textAlign: "center" }}
                >
                  Seja bem-vindo a
                </Text>

                <Flex direction="column" align="center" css={{ gap: "0" }}>
                  <Text
                    size="8"
                    fw="700"
                    variant="primary"
                    css={{
                      lineHeight: 1.1,
                      textAlign: "center",
                      marginBottom: "$2",
                    }}
                  >
                    experiência
                  </Text>

                  <BiztripLogo
                    size="xlg"
                    variant="mobile"
                    css={{
                      height: "164px",
                      paddingBottom: 0,
                      marginTop: 0,
                    }}
                  />
                </Flex>
              </Flex>

              <Text
                size="3"
                css={{
                  color: "$neutral-600",
                  lineHeight: 1.5,
                  textAlign: "center",
                  maxWidth: "340px",
                  mb: "$2",
                }}
              >
                Crie sua conta biztrip e transforme a gestão de viagens da sua empresa!
              </Text>

              <Button
                variant="primary"
                size="mdl"
                rounded
                fullWidth
                onClick={goToContact}
                css={{
                  fontSize: "$lg",
                  fontWeight: 600,
                  "&:focus": {
                    boxShadow: "none",
                  },
                }}
              >
                Começar agora ✈️
              </Button>
            </StepContent>
          )}

          {activeStep === 1 && (
            <StepContent>
              <form
                style={{ width: "100%" }}
                onSubmit={(e) => {
                  e.preventDefault();
                  goToCompany();
                }}
              >
                <Flex direction="column" gap="5" css={{ width: "100%" }}>
                  <FormControl name="cnpj" control={control}>
                    <FieldLabel>CNPJ</FieldLabel>
                    <MaskedInput mask={MaskUtils.MASK_CNPJ} placeholder="00.000.000/0000-00" />
                    <FieldError name="cnpj" />
                  </FormControl>

                  <FormControl name="companyName" control={control}>
                    <FieldLabel>Razão social</FieldLabel>
                    <TextInput placeholder="Razão social da empresa" />
                    <FieldError name="companyName" />
                  </FormControl>

                  <FormControl name="tradingName" control={control}>
                    <FieldLabel>Nome fantasia</FieldLabel>
                    <TextInput placeholder="Nome fantasia da empresa" />
                    <FieldError name="tradingName" />
                  </FormControl>

                  <FormControl name="phone" control={control}>
                    <FieldLabel>Telefone</FieldLabel>
                    <PhoneInput placeholder="+55 (11) 99999-9999" />
                    <FieldError name="phone" />
                  </FormControl>

                  <Flex justify="between" gap="3">
                    <Button variant="outline" size="mdl" rounded onClick={goBack} css={{ flex: 1 }}>
                      Voltar
                    </Button>
                    <Button variant="primary" size="mdl" rounded type="submit" css={{ flex: 1 }}>
                      Continuar
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </StepContent>
          )}

          {activeStep === 2 && (
            <StepContent>
              <form
                style={{ width: "100%" }}
                onSubmit={handleSubmit((data) => handleOnboard(data))}
              >
                <Flex direction="column" gap="5" css={{ width: "100%" }}>
                  <FormControl name="email" control={control}>
                    <FieldLabel>E-mail da empresa</FieldLabel>
                    <TextInput placeholder="contato@empresa.com.br" />
                    <FieldError name="email" />
                  </FormControl>

                  <FormControl name="averageTravelSpend" control={control}>
                    <FieldLabel>Gasto médio em viagens</FieldLabel>
                    <NumberInput prefix="R$ " placeholder="R$ 0,00" allowNegative={false} />
                    <FieldError name="averageTravelSpend" />
                  </FormControl>

                  <FormControl name="referredBy" control={control}>
                    <FieldLabel>Indicado por</FieldLabel>
                    <TextInput placeholder="Nome de quem indicou" />
                    <FieldError name="referredBy" />
                  </FormControl>

                  <Flex justify="between" gap="3">
                    <Button variant="outline" size="mdl" rounded onClick={goBack} css={{ flex: 1 }}>
                      Voltar
                    </Button>
                    <Button
                      variant="primary"
                      size="mdl"
                      rounded
                      type="submit"
                      disabled={isOnboarding}
                      css={{ flex: 1 }}
                    >
                      {isOnboarding ? "Criando..." : "Criar empresa"}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </StepContent>
          )}

          {activeStep === 3 && isSuccess && result && (
            <StepContent>
              <Text
                size="6"
                fw="700"
                css={{ color: "$neutrals-darkest", lineHeight: 1.3, textAlign: "center" }}
              >
                Empresa criada com sucesso! 🎉
              </Text>

              <Text
                size="3"
                css={{
                  color: "$neutral-600",
                  lineHeight: 1.5,
                  textAlign: "center",
                  maxWidth: "340px",
                }}
              >
                Sua empresa está em período de teste por 15 dias. Compartilhe o link abaixo com o
                responsável para definir a senha de acesso:
              </Text>

              <Button
                variant="primary"
                size="mdl"
                rounded
                fullWidth
                onClick={() => window.open(result.resetPasswordUrl, "_blank")}
                css={{ fontSize: "$lg", fontWeight: 600 }}
              >
                Abrir link de acesso
              </Button>

              <Text
                size="2"
                css={{ color: "$neutral-400", textAlign: "center", wordBreak: "break-all" }}
              >
                {result.resetPasswordUrl}
              </Text>
            </StepContent>
          )}
        </CardContainer>
      </PageContainer>
    </FormProvider>
  );
};
